import axios, { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import * as HttpStatus from 'http-status-codes';
import { AUTH_STORAGE_KEY, AUTH_HEADER, AUTH_SCHEME, AUTH_PATHS, LAST_TOKEN_SET_KEY, LAST_TOKEN_SET_GRACE_MS } from '../features/auth/constants';
import { emitTokenRefresh } from './sessionEventBus';
import config from '../config';

import { createLogger } from '../utils/logger';
const logger = createLogger('utils');

export class HttpRequest {

  private method: Method | undefined;
  private timeout: number | undefined;
  private readonly initialHeaders = {
    'accept': 'application/json',
    'content-type': 'application/json'
  };

  constructor(private host: string, private responseType: any = '') {
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /**
   * GET request
   */
  async get(path: string = '', params: object = {}, headers: Record<string, string> = {}) {
    this.method = 'GET';
    return await this.request(path, params, headers);
  }

  /**
   * GET request with body
   */
  async getWithBody(path: string, params: object = {}, headers: Record<string, string> = {}) {
    this.method = 'GET';
    return await this.request(path, params, headers, true);
  }

  /**
   * POST request
   */
  async post(data: any = {}, path: string = '', headers: Record<string, string> = this.initialHeaders, timeout: number = 0) {
    this.method = 'POST';
    this.timeout = timeout;
    return await this.request(path, data, headers);
  }

  /**
   * PUT request
   */
  async put(path: string, data: any = {}, headers: Record<string, string> = this.initialHeaders) {
    this.method = 'PUT';
    return await this.request(path, data, headers);
  }

  /**
   * PATCH request
   */
  async patch(path: string, data: any = {}, headers: Record<string, string> = this.initialHeaders) {
    this.method = 'PATCH';
    return await this.request(path, data, headers);
  }

  /**
   * DELETE request
   */
  async delete(path: string, data: object = {}, headers: Record<string, string> = this.initialHeaders) {
    this.method = 'DELETE';
    return this.request(path, data, headers);
  }

  /**
   * Core request handler
   */
  async request(path: string, data: any, headers: Record<string, string>, isWithBody: boolean = false): Promise<AxiosResponse> {

    const config: AxiosRequestConfig = {
      method : this.method,
      url:(!path) ? this.host : `${this.host}/${path}`,
      headers: this.initialHeaders
    };

    if (Object.keys(data).length) {
      const paramType: 'params' | 'data' = (this.method === 'GET' && !isWithBody) ? 'params' : 'data';
      config[paramType] = data;
    }

    if (Object.keys(headers).length) {
      config['headers'] = headers;
    }

    // Add JWT token to request headers if available
    const token = localStorage.getItem(AUTH_STORAGE_KEY);
    if (token && config.headers) {
      config.headers[AUTH_HEADER] = `${AUTH_SCHEME} ${token}`;
    }

    if (this.responseType) {
      config['responseType'] = this.responseType;
    }

    if (this.timeout) {
      config['timeout'] = this.timeout;
    }

    return new Promise((resolve, reject) => {
      axios(config).then((response: AxiosResponse) => {
        resolve(response);
      }).catch((error) => {
        // Los errores 401/403 ahora son manejados por el response interceptor
        // que incluye retry logic automático
        const errorResponse = error.response || { status: HttpStatus.INTERNAL_SERVER_ERROR };
        resolve(errorResponse);
      });
    });
  }

  /**
   * Setup request interceptor to check and refresh tokens before requests
   */
  private setupRequestInterceptor() {
    const self = this;
    const authEndpoint = `${config.endpoints.auth}/${AUTH_PATHS.LOGIN}`;
    axios.interceptors.request.use(
      async (config) => {
        // No agregar token ni intentar refresh en endpoints de autenticación
        const isAuthEndpoint = config.url?.includes(authEndpoint);

        if (isAuthEndpoint) {
          return config; // Dejar pasar sin modificar
        }

        const token = localStorage.getItem(AUTH_STORAGE_KEY);
        if (token) {
          // No hacer refresh proactivo justo después del login (evita "Session Expired" tras login)
          const lastSet = sessionStorage.getItem(LAST_TOKEN_SET_KEY);
          const withinGrace = lastSet && (Date.now() - parseInt(lastSet, 10)) < LAST_TOKEN_SET_GRACE_MS;
          // ✅ Renovación proactiva si el token está cerca de expirar y no estamos en la gracia post-login
          if (!withinGrace && self.isTokenNearExpiry(token)) {
            try {
              logger.info('🔔 Token near expiry, proactively renewing...');
              const newToken = await self.refreshAccessToken();
              config.headers[AUTH_HEADER] = `${AUTH_SCHEME} ${newToken}`;
              // Notify session manager about successful token refresh
              emitTokenRefresh(true, 'httpClient-proactive');
            } catch (error) {
              logger.warn('⚠️ Error proactively renewing token:', error);
              // Notify session manager about failed token refresh
              emitTokenRefresh(false, 'httpClient-proactive');
              // Si falla el refresh, usar token actual y dejar que el response interceptor maneje el error
              config.headers[AUTH_HEADER] = `${AUTH_SCHEME} ${token}`;
            }
          } else {
            config.headers[AUTH_HEADER] = `${AUTH_SCHEME} ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  /**
   * Setup response interceptor with automatic retry logic for 401/403 errors
   */
  private setupResponseInterceptor() {
    const self = this;
    axios.interceptors.response.use(
      (response) => response, // Peticiones exitosas pasan sin modificación
      async (error) => {
        const originalRequest = error.config;
        const authEndpoint = `${config.endpoints.auth}/${AUTH_PATHS.LOGIN}`;
        // No intentar refresh en endpoints de autenticación
        const isAuthEndpoint = originalRequest?.url?.includes(authEndpoint);

        // Si es error 401/403 y no hemos intentado refresh aún y NO es endpoint de auth
        if (
          (error.response?.status === 401 || error.response?.status === 403) &&
          !originalRequest?._retry &&
          !isAuthEndpoint
        ) {
          originalRequest._retry = true; // Marcar para evitar loops infinitos

          // ✅ Intentar refresh automático tanto en desarrollo como en producción
          try {
            logger.info('🔄 Token expired detected, attempting automatic renewal...');
            const newToken = await self.refreshAccessToken();

            // Actualizar header de la petición original con el nuevo token
            originalRequest.headers[AUTH_HEADER] = `${AUTH_SCHEME} ${newToken}`;

            logger.info('✅ Token renewed successfully, retrying original request...');
            // Notify session manager about successful token refresh
            emitTokenRefresh(true, 'httpClient-reactive');
            // Reintentar petición original con nuevo token
            return axios(originalRequest);
          } catch (refreshError) {
            logger.error('❌ Error renewing token:', refreshError);
            // Notify session manager about failed token refresh
            emitTokenRefresh(false, 'httpClient-reactive');

            // Mostrar notificación informativa antes de redirigir
            import('antd').then(({ notification }) => {
              notification.warning({
                key: 'token-refresh-failed',
                message: 'Session Expired',
                description: 'Unable to renew your session automatically. Please login again.',
                duration: 5,
                placement: 'topRight',
              });
            }).catch(() => {
              logger.info('Could not show token refresh failed notification');
            });

            // Limpiar tokens corruptos/expirados
            localStorage.removeItem(AUTH_STORAGE_KEY);
            localStorage.removeItem('refreshToken');

            // Solo redirigir si no estamos ya en login
            setTimeout(() => {
              if (!window.location.pathname.includes('login')) {
                logger.info('Redirecting to login due to authentication error...');
                window.location.href = '/login';
              }
            }, 1000);

            return Promise.reject(refreshError);
          }
        }

        // Para otros errores o si ya se intentó refresh, rechazar normalmente
        return Promise.reject(error);
      }
    );
  }

  /**
   * Internal method to refresh access token using refresh token
   * Works in both development and production environments
   */
  private async refreshAccessToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    logger.info('🔄 Refreshing access token...');

    try {
      // Corregir URL - asegurar que NO tenga paths extra
      const baseUrl = this.host.replace(/\/[^\/]*$/, ''); // Remover cualquier path final
      const refreshUrl = `${baseUrl}/auth/refresh`;

      logger.info(`📡 Calling refresh endpoint: ${refreshUrl}`);

      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${refreshToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        const data = await response.json();
        const { accessToken } = data.data;
        localStorage.setItem('accessToken', accessToken);
        logger.info('✅ Token renewed successfully');
        // Note: emitTokenRefresh is called by the calling function
        return accessToken;
      } else {
        const errorText = await response.text();
        logger.error(`❌ Refresh failed with status ${response.status}:`, errorText);
        throw new Error(`HTTP ${response.status}: Failed to refresh token`);
      }
    } catch (error) {
      logger.error('❌ Error refreshing token:', error);
      // Si el refresh falla, limpiar tokens y forzar re-login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      throw error;
    }
  }

  /**
   * Check if token is close to expiring (within 5 minutes)
   */
  private isTokenNearExpiry(token: string): boolean {
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = decoded.exp - currentTime;
      // Renovar si quedan menos de 5 minutos (300 segundos)
      return timeUntilExpiry < 300;
    } catch {
      return false; // Si no se puede decodificar, asumir que no está próximo a expirar
    }
  }
} 